import { MegaphoneIcon, SearchIcon, SyncIcon, XIcon } from '@primer/octicons-react';
import { useRouter } from 'next/router';
import { ComponentProps, Suspense } from 'react';
import { graphql } from 'react-relay';

import AppDialogConnectionDataTable from '../../components/appDialog/AppDialogConnectionDataTable';
import AppDialogCreateDialog from '../../components/appDialog/AppDialogCreateDialog';
import AppDialogWindowPaginator from '../../components/appDialog/AppDialogWindowPaginator';
import Button from '../../components/core/Button';
import DateRangeField from '../../components/core/DateRangeField';
import DialogButton from '../../components/core/DialogButton';
import EmptyState from '../../components/core/EmptyState';
import EnumPairSearchOverlayTokenField from '../../components/core/EnumPairSearchOverlayTokenField';
import ErrorBoundary from '../../components/core/ErrorBoundary';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import IconButton from '../../components/core/IconButton';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import QueryFormik from '../../components/core/QueryFormik';
import Spinner from '../../components/core/Spinner';
import Stack from '../../components/core/Stack';
import Text from '../../components/core/Text';
import View from '../../components/core/View';
import UserPaginatorSearchOverlayTokenField from '../../components/user/UserPaginatorSearchOverlayTokenField';
import { usePaginationContext } from '../../contexts/PaginationContext';
import useInitialValuesFromParsedUrlQuery from '../../hooks/useInitialValuesFromParsedUrlQuery';
import useToast from '../../hooks/useToast';
import {
  appDialog_appDialogsQuery,
  AppDialogStatusEnum,
} from '../../relay/__generated__/appDialog_appDialogsQuery.graphql';
import { numberWithCommas } from '../../utils/number';
import { NextPage } from '../_app';

import AppDialogId from './[appDialogId]';

const appDialogsForAppDialog = graphql`
  query appDialog_appDialogsQuery($filters: AppDialogFilter, $order: AppDialogOrder, $page: Int, $pageSize: Int) {
    ...AppDialogWindowPaginator_query @arguments(filters: $filters, order: $order, page: $page, pageSize: $pageSize)
  }
`;

type Props = {};

const AppDialog: NextPage<Props> = () => {
  const router = useRouter();
  const { toast } = useToast();

  const { initialValues, setParsedUrlQuery } = useInitialValuesFromParsedUrlQuery({
    search: { type: 'string' },
    status_In: { type: 'string', multiple: true },
    createdById_In: { type: 'string', multiple: true },
    startAt_Gte: { type: 'string' },
    endAt_Lte: { type: 'string' },
    order: { type: 'string' },
    page: { type: 'number' },
  });
  const { pageSize } = usePaginationContext();

  const { search, status_In, createdById_In, startAt_Gte, endAt_Lte, order, page } = initialValues;

  return (
    <View>
      <Head siteTitle={'만능 다이얼로그'} />
      <ErrorBoundary>
        <QueryFormik<appDialog_appDialogsQuery>
          query={appDialogsForAppDialog}
          staticVariables={{ pageSize }}
          initialValues={{
            filters: {
              search,
              status_In: status_In as AppDialogStatusEnum[],
              createdById_In,
              startAt_Gte,
              endAt_Lte,
            },
            order: order || '-startAt',
            page,
          }}
          options={{ fetchPolicy: 'store-and-network' }}
          onSubmit={(values) => setParsedUrlQuery({ ...values.filters, order, page }, { scroll: false })}
          enableReinitialize
        >
          {({ values: { filters, order }, handleSubmit, submitForm, resetForm, setFieldValue }, queryReference) => {
            const handleReset = () => {
              resetForm({ values: { order } });
              setParsedUrlQuery({ order }, { scroll: false });
            };

            const handleChangeOrderActionMenu: ComponentProps<typeof QueryFormik.OrderActionMenuButton>['onChange'] = (
              newValue,
            ) => {
              setParsedUrlQuery({ ...filters, order: newValue }, { scroll: false });
            };

            const isPeriodSelected = filters?.startAt_Gte || filters?.startAt_Lte;

            return (
              <View>
                <Grid sx={{ alignItems: 'center' }}>
                  <Grid.Unit size={'max'}>
                    <Text as={'h1'}>만능 다이얼로그</Text>
                  </Grid.Unit>
                  <Grid.Unit size={'min'}>
                    <DialogButton
                      size={'large'}
                      renderDialog={({ isOpen, closeDialog }) => (
                        <AppDialogCreateDialog
                          isOpen={isOpen}
                          onDismiss={closeDialog}
                          config={{
                            onCompleted: () => {
                              toast('만능 다이얼로그가 생성됐어요!', 'success');
                              handleSubmit();
                              closeDialog();
                            },
                            onError: (e) => {
                              console.log(e);
                              toast('다시 생성해 주세요', 'error');
                            },
                          }}
                        />
                      )}
                      variant={'primary'}
                    >
                      생성하기
                    </DialogButton>
                  </Grid.Unit>
                </Grid>
                <Grid>
                  <Grid.Unit size={'min'}>
                    <QueryFormik.FilterSearchTextField
                      typename={'AppDialogFilter'}
                      label={'Search'}
                      labelConfig={{ visuallyHidden: true }}
                      name={'filters.search'}
                      size={'large'}
                      autoComplete={'off'}
                      leadingVisual={SearchIcon}
                      debounce
                      onChange={(e) => {
                        setFieldValue('filters.search', e.target.value);
                        setTimeout(() => submitForm(), 0);
                      }}
                    />
                  </Grid.Unit>
                  <Grid.Unit size={'max'}>
                    <View sx={{ display: 'flex', justifyContent: 'end' }}>
                      <Stack gapX={2}>
                        <Stack.Item>
                          <Button leadingIcon={SyncIcon} variant={'plain'} onClick={handleReset}>
                            초기화
                          </Button>
                        </Stack.Item>
                        <Stack.Item>
                          <QueryFormik.OrderActionMenuButton
                            typename={'AppDialogOrder'}
                            orders={['startAt', 'endAt']}
                            onChange={handleChangeOrderActionMenu}
                          />
                        </Stack.Item>
                      </Stack>
                    </View>
                  </Grid.Unit>
                </Grid>
                <View sx={{ marginTop: 5 }}>
                  <QueryFormik.FilterGrid
                    onChange={(filters) => {
                      setParsedUrlQuery({ ...filters, order }, { scroll: false });
                    }}
                  >
                    <QueryFormik.FilterGridUnit size={1} title={'상태'}>
                      <EnumPairSearchOverlayTokenField
                        typename={'AppDialogStatusEnum'}
                        label={'상태'}
                        name={'filters.status_In'}
                        placeholder={'상태 선택'}
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'제작자'}>
                      <UserPaginatorSearchOverlayTokenField
                        label={'제작자'}
                        name={'filters.createdById_In'}
                        placeholder={'제작자 선택'}
                      />
                    </QueryFormik.FilterGridUnit>
                    <QueryFormik.FilterGridUnit size={[1, 1, 1 / 2]} title={'진행일'}>
                      <Grid sx={{ alignItems: 'center' }} wrap={false}>
                        <Grid.Unit size={'max'}>
                          <DateRangeField
                            label={'진행일'}
                            labelConfig={{ visuallyHidden: true }}
                            name={['filters.startAt_Gte', 'filters.endAt_Lte']}
                            placeholder={['시작일', '종료일']}
                          />
                        </Grid.Unit>
                        <Grid.Unit size={'min'}>
                          <IconButton
                            icon={XIcon}
                            aria-label={'reset created period'}
                            variant={'plain'}
                            disabled={!isPeriodSelected}
                            onClick={() => {
                              setFieldValue('filters', { ...filters, startAt_Gte: undefined, endAt_Lte: undefined });
                            }}
                          />
                        </Grid.Unit>
                      </Grid>
                    </QueryFormik.FilterGridUnit>
                  </QueryFormik.FilterGrid>
                </View>
                <View sx={{ marginTop: 5 }}>
                  <ErrorBoundary key={queryReference?.fetchKey}>
                    <Suspense fallback={<Spinner />}>
                      <QueryFormik.PreloadedQueryRenderer<appDialog_appDialogsQuery>>
                        {(queryReference) => (
                          <AppDialogWindowPaginator
                            fragmentReference={queryReference}
                            onLoadPage={(page) => setParsedUrlQuery({ ...filters, order, page })}
                          >
                            {({ appDialogs }, { renderPagination }) => (
                              <>
                                <View>
                                  <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.muted' }}>
                                    총 {numberWithCommas(appDialogs.totalCount || 0)}개
                                  </Text>
                                </View>
                                <View sx={{ marginTop: 3 }}>
                                  <AppDialogConnectionDataTable
                                    appDialogConnection={appDialogs}
                                    onRowClick={({ id }) => router.push({ pathname: `/appDialog/${id}` })}
                                    emptyState={
                                      <View sx={{ paddingY: 3 }}>
                                        <EmptyState
                                          title={'필터에 맞는 결과가 없어요'}
                                          description={'다른 필터로 다시 시도해보세요.'}
                                        />
                                      </View>
                                    }
                                  />
                                </View>
                                <View sx={{ display: 'flex', justifyContent: 'center', marginTop: 3 }}>
                                  {renderPagination?.()}
                                </View>
                              </>
                            )}
                          </AppDialogWindowPaginator>
                        )}
                      </QueryFormik.PreloadedQueryRenderer>
                    </Suspense>
                  </ErrorBoundary>
                </View>
              </View>
            );
          }}
        </QueryFormik>
      </ErrorBoundary>
    </View>
  );
};

AppDialog.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
AppDialog.authenticated = true;
AppDialog.routes = [
  {
    id: 'appDialog',
    name: '만능 다이얼로그',
    pathname: '/appDialog',
    icon: MegaphoneIcon,
    permissions: ['app_dialog_read'],
  },
  ...AppDialogId.routes,
];

export default AppDialog;
